import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { FileStack, Plus, Pencil, Trash2, Loader2, Lock } from "lucide-react";

const PLACEHOLDERS = ["{client_name}", "{address}", "{city}", "{state}", "{zip}", "{ssn_last4}", "{dob}", "{bureau}", "{creditor}", "{date}"];
const EMPTY = { name: "", category: "General", body: "" };

export default function LetterTemplates() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/letter-templates").then((r) => setRows(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (t) => { setEditing(t); setForm({ name: t.name, category: t.category || "General", body: t.body }); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Template name required");
    if (!form.body.trim()) return toast.error("Template body required");
    setSaving(true);
    try {
      if (editing) await api.put(`/letter-templates/${editing.id}`, form);
      else await api.post("/letter-templates", form);
      toast.success(editing ? "Template updated" : "Template created");
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Save failed"); } finally { setSaving(false); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try { await api.delete(`/letter-templates/${t.id}`); toast.success("Template deleted"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || "Delete failed"); }
  };

  return (
    <div className="animate-fade-up" data-testid="letter-templates-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-xl bg-primary/15 text-primary flex items-center justify-center"><FileStack className="h-6 w-6" /></div>
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight leading-none">Letter Templates</h1>
            <p className="text-muted-foreground text-sm mt-1.5">Reusable dispute wording you can drop into any client letter.</p>
          </div>
        </div>
        <Button onClick={openNew} className="rounded-lg" data-testid="add-template-btn"><Plus className="h-4 w-4 mr-1" /> New Template</Button>
      </div>

      <Card className="p-4 bg-card/40 border-border mb-6">
        <p className="text-xs text-muted-foreground">
          Use placeholders that auto-fill from the client and letter when inserted:{" "}
          {PLACEHOLDERS.map((p) => (<code key={p} className="mx-0.5 px-1.5 py-0.5 rounded bg-white/5 text-primary text-[11px]">{p}</code>))}
        </p>
      </Card>

      {loading ? (
        <div className="text-muted-foreground flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : rows.length === 0 ? (
        <Card className="p-10 bg-card/40 border-border text-center text-muted-foreground" data-testid="templates-empty">No templates yet. Create your first one.</Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {rows.map((t) => (
            <Card key={t.id} className="p-5 bg-card/60 border-border flex flex-col" data-testid={`template-${t.id}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0">
                  <div className="font-display font-semibold truncate">{t.name}</div>
                  <Badge variant="secondary" className="mt-1 text-[10px]">{t.category || "General"}</Badge>
                </div>
                {t.is_default && <span className="text-[10px] text-muted-foreground flex items-center gap-1 shrink-0"><Lock className="h-3 w-3" /> Default</span>}
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed line-clamp-4 whitespace-pre-wrap flex-1">{t.body}</p>
              <div className="flex gap-2 mt-4 pt-3 border-t border-border/60">
                <Button size="sm" variant="secondary" className="rounded-lg flex-1" onClick={() => openEdit(t)} data-testid={`edit-template-${t.id}`}><Pencil className="h-3.5 w-3.5 mr-1" /> Edit</Button>
                <Button size="sm" variant="ghost" className="rounded-lg text-muted-foreground hover:text-destructive" onClick={() => remove(t)} data-testid={`delete-template-${t.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card/95 backdrop-blur-xl border-border max-w-2xl max-h-[90vh] overflow-y-auto" description="Create or edit a reusable dispute letter template">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit Template" : "New Template"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Debt Validation Request" data-testid="template-name" /></div>
              <div className="space-y-2"><Label>Category</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="e.g. Collections" data-testid="template-category" /></div>
            </div>
            <div className="space-y-2">
              <Label>Body</Label>
              <Textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="font-mono text-xs leading-relaxed min-h-[320px] bg-background/50" placeholder="Use {client_name}, {bureau}, {creditor}, {date} …" data-testid="template-body" />
            </div>
          </div>
          <DialogFooter><Button onClick={save} disabled={saving} className="rounded-lg" data-testid="save-template-btn">{saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null} {editing ? "Save Changes" : "Create Template"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
